<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte'
  import { replace, querystring } from 'svelte-spa-router'
  import { get } from 'svelte/store'
  import { tStore } from '../../i18n'
  import { toScore } from '../../composables/coerce'
  import { procurementsApi, ALLOWED_COUNTRY_CODES } from '../../api/procurements'
  import type {
    ProcurementFilters as ProcurementFiltersType,
    ProcurementSortField,
    ProcurementSummary,
    SortOrder,
    CountryCode,
  } from '../../api/procurements'
  import { retrievalsApi, type RetrievalRun } from '../../api/retrievals'
  import { sourcesApi, type Source } from '../../api/sources'
  import { buildSourceLabels } from '../../composables/sourceNames'
  import { savedFiltersApi, type SavedFilter } from '../../api/savedFilters'
  import { tenderFiltersApi, type TenderFilter } from '../../api/tenderFilters'
  import { activeFilterId, initActiveFilterId, setActiveFilterId } from '../../stores/tenderFilter'
  import { aiAnalysisApi } from '../../api/aiAnalysis'
  import { analysisOutcome, createAnalysisPoller } from '../../api/aiAnalysis/poller'
  import { isApiError } from '../../api/errors'
  import { requestNotificationRefresh } from '../../stores/inAppNotifications'
  import PageHeader from '../../components/PageHeader.svelte'
  import Dropdown from '../../components/Dropdown.svelte'
  import { formatDateTime } from '../../composables/format'
  import { useAbortable } from '../../composables/useAbortable'
  import { useToast } from '../../composables/useToast'
  import KpiStrip from './dashboard/KpiStrip.svelte'
  import RecentRunsCard from './dashboard/RecentRunsCard.svelte'
  import TopSourcesCard from './dashboard/TopSourcesCard.svelte'
  import ProcurementFilters from './dashboard/ProcurementFilters.svelte'
  import ProcurementTable from './dashboard/ProcurementTable.svelte'
  import ProcurementPager from './dashboard/ProcurementPager.svelte'
  import DeleteConfirmModal from './dashboard/DeleteConfirmModal.svelte'
  import BulkAiResultsModal, { type BulkTenderResult } from './dashboard/BulkAiResultsModal.svelte'
  import AiAnalysisSpinner from './dashboard/AiAnalysisSpinner.svelte'
  import { VALUE_RANGE_PRESETS } from './dashboard/constants'
  import { latestRunPerActiveSource } from './dashboard/recentRuns'

  // Page size options — match the backend's MAX_LIMIT (200) so a single
  // request never asks for more than the API allows.
  const PAGE_SIZES = [10, 25, 50, 100, 200] as const
  const DEFAULT_PAGE_SIZE = 25

  // Match the backend's ProcurementRoutes.MAX_BULK_AI_IDS so a bulk selection
  // never reaches "Run Bulk AI" only to have the whole request rejected —
  // page sizes go up to 200, well past the bulk cap.
  const MAX_BULK_SELECTION = 50

  let procurements = $state<ProcurementSummary[]>([])
  let runs = $state<RetrievalRun[]>([])
  let sources = $state<Source[]>([])
  let savedFilters = $state<SavedFilter[]>([])
  let activeSavedFilterId = $state<number | undefined>(undefined)
  let tenderFilters = $state<TenderFilter[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)
  let totalCount = $state<number | null>(null)
  let sourceTotals = $state<{ sourceName: string; total: number }[]>([])
  // RecentRunsCard's table content height, measured via bind:clientHeight —
  // TopSourcesCard caps its scrollable list to this (rather than a fixed
  // row count) so it fills the card without inflating RecentRunsCard's own
  // height in turn (both cards share identical h3/padding chrome, so the
  // content heights alone need to match, not the full card heights).
  let recentRunsTableHeight = $state<number | undefined>(undefined)

  // Page-size as state, page number is derived from offset / pageSize.
  let pageSize = $state(DEFAULT_PAGE_SIZE)
  let offset = $state(0)
  // True until the user pages back to 0 or shrinks the result set — drives the
  // Next button's disabled state. We can't compute total pages without a count
  // endpoint, so we infer "more pages exist" from a full page.
  let hasMore = $state(false)

  let baseFilters = $state<ProcurementFiltersType>({})
  // True only while the current baseFilters.sourceId was set by a Top
  // Sources click — distinguishes that from the user picking a source
  // manually via the "Source" dropdown, which must stay freely editable.
  // Drives the dropdown's disabled state in ProcurementFilters; reset to
  // false everywhere else sourceId can change (deselect, Clear filters,
  // applying a saved filter) so the lock never outlives the selection that
  // caused it.
  let sourceLockedByTopSources = $state(false)
  let filtersOpen = $state(false)
  let lastAppliedProcurementFilters: ProcurementFiltersType | null = null
  // The `filterBase` (count/source-totals shape, without paging) from the
  // last confirmed loadProcurements() call — background stats refresh reuses
  // this so it never picks up a baseFilters/sort edit the user hasn't
  // confirmed yet, same rule as lastAppliedProcurementFilters above.
  let lastAppliedFilterBase: ProcurementFiltersType | null = null

  // Column sort state. Undefined field = backend default (newest published
  // first). Clicking a sortable header sets the field and toggles asc/desc.
  let sortField = $state<ProcurementSortField | undefined>(undefined)
  let sortDir = $state<SortOrder>('desc')

  // Which Value range dropdown entry is active, when filters.currency is a
  // specific currency (EUR/CHF) — a preset id, or 'custom' ("Any value" in
  // the dropdown, which reveals free-form Min/Max inputs). Meaningless
  // (dropdown not rendered) while filters.currency is unset ("Any currency").
  let valueRangeChoice = $state<'custom' | string>('custom')

  const { start } = useAbortable()
  const toast = useToast()

  // Per-row in-flight guard for the "Run AI" button. A row stays "pending" —
  // and its button disabled — from the click until the analysis actually
  // finishes, not just until the trigger request returns.
  let aiPending = $state<Set<number>>(new Set())

  // Drives the persistent bottom-right spinner for any in-flight AI trigger —
  // a single-tender "Run AI" click or a bulk run of any size. It stays visible
  // as long as at least one tracked tender hasn't reached a terminal state
  // yet, so for a bulk batch it only disappears once the last tender finishes.
  let aiSpinnerIds = $state<Set<number>>(new Set())
  let showAiSpinner = $derived(aiSpinnerIds.size > 0)

  // Bulk AI Analysis selection mode. Two controls (top of the list, next to
  // "All Procurements", and bottom, next to "Per Page") toggle the same
  // state so they always agree.
  let bulkMode = $state(false)
  let selectedIds = $state<Set<number>>(new Set())
  let bulkRunning = $state(false)
  // Accumulates every tender triggered since bulk mode was last (re)entered —
  // a durable record of exactly which tenders succeeded/failed, unlike a
  // toast, it can't be missed. Cleared on exiting bulk selection, which now
  // includes closing the results panel (see exitBulkMode) — the panel is the
  // only place that record is visible, so once it's closed there's nothing
  // left to preserve it for.
  let bulkResults = $state<BulkTenderResult[] | null>(null)
  // Separate from bulkResults: whether the panel is currently shown.
  let bulkResultsVisible = $state(false)
  // Tenders currently running a bulk-triggered analysis, tracked separately
  // from selectedIds and bulkResults/bulkResultsVisible — and NOT reset by
  // exitBulkMode — so a still-running batch keeps occupying slots even after
  // the panel is closed and bulk mode is re-entered. The selection cap is
  // "selected + still-running", not just "selected" — otherwise closing the
  // modal and reselecting a fresh 50 would run 100 at once.
  let bulkPendingIds = $state<Set<number>>(new Set())

  let occupiedBulkSlots = $derived(selectedIds.size + bulkPendingIds.size)

  let deleteTarget = $state<ProcurementSummary | null>(null)
  let deleteInFlight = $state(false)

  function confirmDelete(p: ProcurementSummary) {
    deleteTarget = p
  }

  function cancelDelete() {
    if (deleteInFlight) return
    deleteTarget = null
  }

  async function executeDeletion() {
    if (!deleteTarget || deleteInFlight) return
    const id = deleteTarget.id
    deleteInFlight = true
    try {
      await procurementsApi.delete(id)
      procurements = procurements.filter((p) => p.id !== id)
      if (totalCount !== null) totalCount -= 1
      toast.show($tStore('views.dashboard.deleteSuccess'), 'success')
      deleteTarget = null
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : $tStore('views.dashboard.deleteError'),
        'error',
      )
    } finally {
      deleteInFlight = false
    }
  }

  // Track asynchronous work through one lightweight batch request. Only a
  // terminal server status finishes a run; a slow download is still running.
  const LIST_REFRESH_INTERVAL_MS = 5000
  const analysisPoller = createAnalysisPoller()
  let listRefreshTimer: ReturnType<typeof setInterval> | null = null
  let listRefreshController: AbortController | null = null
  let listRefreshInFlight = false

  // Background refresh for Recent runs / Last successful run / Top sources /
  // the total count — separate from the list refresh above, which only
  // touches `procurements`. While at least one run is still "running", poll
  // at the fast interval so a completion shows up quickly; otherwise fall
  // back to a slow heartbeat so a run that starts while the page is idle
  // still appears without a manual refresh (mirrors IngestionRunsView).
  const STATS_FAST_INTERVAL_MS = 5000
  const STATS_HEARTBEAT_INTERVAL_MS = 30000
  let statsRefreshTimer: ReturnType<typeof setTimeout> | null = null
  let statsRefreshController: AbortController | null = null
  let statsRefreshInFlight = false

  // Stops this row's poll loop without touching aiPending/aiSpinnerIds/bulk
  // state — used by cancel so a stale in-flight fetch can't land after the
  // cancel and clobber the freshly-cancelled UI state with an older status.
  function stopPolling(id: number) {
    analysisPoller.stop(id)
  }

  // Fire the same AI analysis the detail page's "Calculate AI" button triggers
  // (POST /procurements/{id}/ai-analysis), then keep the button disabled while
  // polling for completion. The persistent spinner (not a toast) is the
  // "started" feedback now - it appears immediately and stays up for the
  // whole poll, instead of a notification that can be missed and vanishes
  // after a few seconds.
  async function runAi(id: number) {
    if (aiPending.has(id)) return
    aiPending = new Set(aiPending).add(id)
    aiSpinnerIds = new Set(aiSpinnerIds).add(id)
    try {
      await aiAnalysisApi.trigger(id)
      pollAi(id)
    } catch (err) {
      toast.show(err instanceof Error ? err.message : String(err), 'error')
      finishAi(id)
    }
  }

  // onResult (used by the bulk flow) reports the same terminal outcome this
  // function already distinguishes — no separate polling logic is duplicated
  // for bulk; it just also feeds the results modal. When onResult is passed,
  // the per-tender toast is skipped: the bulk results modal is the durable
  // record for many tenders at once, and firing one toast per tender on top
  // of it would just be noise.
  function pollAi(
    id: number,
    onResult?: (id: number, outcome: 'success' | 'noDocuments' | 'failed' | 'cancelled') => void,
  ) {
    analysisPoller.track(id, (status) => {
      if (status) {
        bulkResults =
          bulkResults?.map((row) =>
            row.id === id ? { ...row, phase: status.phase, retryAt: status.retryAt } : row,
          ) ?? null
      }
      // A missing batch-status row means the procurement vanished (for example,
      // it was deleted mid-run); settle that bulk item as failed.
      const outcome = status ? analysisOutcome(status) : 'failed'
      if (!outcome) return
      if (status) {
        procurements = procurements.map((p) =>
          p.id === id ? { ...p, aiScore: status.aiScore } : p,
        )
      }
      if (outcome === 'success') requestNotificationRefresh()
      if (!onResult) {
        const key = {
          success: 'runAiDone',
          failed: 'runAiFailed',
          cancelled: 'runAiCancelled',
          noDocuments: 'runAiNoDocuments',
        }[outcome]
        toast.show(
          $tStore('views.dashboard.' + key),
          outcome === 'failed' ? 'error' : outcome === 'success' ? 'success' : 'info',
        )
      }
      onResult?.(id, outcome)
      finishAi(id)
    })
  }

  function finishAi(id: number) {
    stopPolling(id)
    const next = new Set(aiPending)
    next.delete(id)
    aiPending = next

    if (aiSpinnerIds.has(id)) {
      const nextSpinner = new Set(aiSpinnerIds)
      nextSpinner.delete(id)
      aiSpinnerIds = nextSpinner
    }
  }

  // Cancels the single-tender run in progress for `id`. The backend cancel is
  // cooperative: it either records a fresh 'cancelled' result (first-ever run)
  // or reverts to whatever the tender's previous result was — the client can't
  // tell which without asking, so one more detail fetch pulls the authoritative
  // post-cancel status/score rather than guessing. Polling is only stopped once
  // the cancel call has actually succeeded — if it fails (e.g. network blip),
  // the run is presumably still going, so the existing poll loop is left alone
  // to keep tracking it rather than abandoning it mid-flight.
  async function cancelAi(id: number) {
    if (!aiPending.has(id)) return
    try {
      await aiAnalysisApi.cancel(id)
      stopPolling(id)
      const detail = await procurementsApi.get(id)
      procurements = procurements.map((p) => (p.id === id ? { ...p, aiScore: detail.aiScore } : p))
      toast.show($tStore('views.dashboard.cancelAiDone'), 'success')
      finishAi(id)
      clearBulkPending(id)
    } catch (err) {
      toast.show(
        err instanceof Error ? err.message : $tStore('views.dashboard.cancelAiError'),
        'error',
      )
    }
  }

  // Shared by the explicit "Exit Bulk Selection" toggle and by closing the
  // results panel — both are read as "I'm done with this batch". Running
  // tenders aren't disturbed: aiPending/bulkPendingIds/aiSpinnerIds live
  // outside bulk mode and keep polling in the background regardless.
  function exitBulkMode() {
    bulkMode = false
    selectedIds = new Set()
    bulkResults = null
    bulkResultsVisible = false
  }

  function toggleBulkMode() {
    if (bulkMode) {
      exitBulkMode()
    } else {
      bulkMode = true
    }
  }

  // Returns whether `id` ends up selected — the caller (ProcurementTable)
  // writes this straight into the checkbox's `.checked` DOM property. A plain
  // reactive `checked={selectedIds.has(p.id)}` binding is not enough here:
  // when a click is rejected, the computed value is false both before *and*
  // after (it was never added), so Svelte's diffing sees no change and skips
  // re-applying it — leaving the browser's own native toggle (which already
  // flipped the box on click, independently of our state) stuck showing
  // checked. Returning the authoritative boolean lets the handler force the
  // DOM element back in sync regardless of what Svelte's diff thinks changed.
  function toggleSelect(id: number): boolean {
    if (selectedIds.has(id)) {
      const next = new Set(selectedIds)
      next.delete(id)
      selectedIds = next
      return false
    }
    if (occupiedBulkSlots >= MAX_BULK_SELECTION) {
      toast.show(
        $tStore('views.dashboard.bulkSelectionLimitReached', { max: MAX_BULK_SELECTION }),
        'warning',
      )
      return false
    }
    const next = new Set(selectedIds)
    next.add(id)
    selectedIds = next
    return true
  }

  // Scoped to the *current page*, not the whole selection — selectedIds
  // persists across paging (so a batch can span several pages, up to the
  // cap), so this must only touch this page's rows and leave any selection
  // made on other pages alone. Returns whether every row on this page ends up
  // selected, for the same DOM-forcing reason toggleSelect() does.
  function toggleSelectAllOnPage(): boolean {
    // Rows currently running an analysis can't be selected (their checkbox
    // is disabled in the table), so "select/deselect all" must only ever
    // touch the rows that are actually selectable.
    const pageIds = procurements.filter((p) => !aiPending.has(p.id)).map((p) => p.id)
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
    const next = new Set(selectedIds)
    if (allPageSelected) {
      for (const id of pageIds) next.delete(id)
      selectedIds = next
      return false
    }
    let truncated = false
    for (const id of pageIds) {
      if (next.size + bulkPendingIds.size >= MAX_BULK_SELECTION) {
        truncated = true
        break
      }
      next.add(id)
    }
    if (truncated) {
      toast.show(
        $tStore('views.dashboard.bulkSelectionLimitReached', { max: MAX_BULK_SELECTION }),
        'warning',
      )
    }
    selectedIds = next
    return pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  }

  // Fires the same trigger the individual "Run AI" button uses, once per
  // selected tender via the bulk endpoint (which itself loops the same
  // single-tender workflow server-side), then polls each accepted tender
  // through the same pollAi() used for a single run. One tender's failure
  // never blocks the rest — every outcome lands in bulkResults instead of
  // aborting the batch.
  // Appends to whatever's already in the panel from an earlier batch in this
  // same bulk-mode session, instead of replacing it — otherwise starting a
  // new batch while a previous one is still running would silently drop it
  // from view even though it's still processing. Re-triggering a tender that
  // already has a row (e.g. it finished and got selected again) replaces
  // that one row rather than showing it twice.
  function appendBulkResults(newRows: BulkTenderResult[]) {
    const existing = bulkResults ?? []
    const newIds = new Set(newRows.map((r) => r.id))
    bulkResults = [...existing.filter((r) => !newIds.has(r.id)), ...newRows]
    bulkResultsVisible = true
  }

  async function runBulkAi() {
    if (selectedIds.size === 0 || bulkRunning) return
    bulkRunning = true
    const ids = [...selectedIds]
    const titleById = new Map(procurements.map((p) => [p.id, p.title]))
    appendBulkResults(
      ids.map((id) => ({
        id,
        title: titleById.get(id) ?? String(id),
        status: 'pending',
      })),
    )
    try {
      const response = await aiAnalysisApi.triggerBulk(ids)
      // Tracks only *this* batch's triggered tenders, so a "complete" toast
      // fires once for this click's work — not per tender, and not confused
      // by an overlapping earlier or later batch still in flight.
      const triggeredCount = response.results.filter((r) => r.status === 'triggered').length
      let remaining = triggeredCount
      for (const item of response.results) {
        // Whichever way this id resolves, it's no longer "selected" — it's
        // either now running (occupying a slot in bulkPendingIds until it
        // finishes) or already done and its slot is free again.
        removeFromSelected(item.procurementId)
        if (item.status === 'triggered') {
          aiPending = new Set(aiPending).add(item.procurementId)
          bulkPendingIds = new Set(bulkPendingIds).add(item.procurementId)
          aiSpinnerIds = new Set(aiSpinnerIds).add(item.procurementId)
          pollAi(item.procurementId, (id, outcome) => {
            updateBulkStatus(
              id,
              outcome === 'success'
                ? 'success'
                : outcome === 'noDocuments'
                  ? 'noDocuments'
                  : outcome === 'cancelled'
                    ? 'cancelled'
                    : 'failed',
            )
            clearBulkPending(id)
            remaining -= 1
            if (remaining === 0) {
              toast.show(
                $tStore('views.dashboard.bulkAiCompleted', { count: triggeredCount }),
                'success',
              )
            }
          })
        } else {
          updateBulkStatus(item.procurementId, item.status)
        }
      }
    } catch (err) {
      // The full diagnostic (status, response body, X-Request-Id) is already
      // logged to the console by the api client — the toast just adds enough
      // to spot it there and, for an admin reporting the issue, a requestId
      // to hand over without needing to open DevTools themselves.
      const message = isApiError(err)
        ? `${err.message} (HTTP ${err.status}${err.requestId ? `, req ${err.requestId}` : ''})`
        : err instanceof Error
          ? err.message
          : String(err)
      toast.show(message, 'error')
      // The bulk request itself failed (e.g. network/validation) before any
      // per-tender outcome came back — every row from *this* batch that's
      // still 'pending' never got a chance to run, so none of them occupy a
      // running slot. Scoped to this batch's ids only — an earlier, still
      // in-flight batch's pending rows must not be touched.
      const thisBatch = new Set(ids)
      bulkResults =
        bulkResults?.map((r) =>
          thisBatch.has(r.id) && r.status === 'pending' ? { ...r, status: 'failed' } : r,
        ) ?? null
      for (const id of ids) removeFromSelected(id)
    } finally {
      bulkRunning = false
    }
  }

  function removeFromSelected(id: number) {
    if (!selectedIds.has(id)) return
    const next = new Set(selectedIds)
    next.delete(id)
    selectedIds = next
  }

  function clearBulkPending(id: number) {
    if (!bulkPendingIds.has(id)) return
    const next = new Set(bulkPendingIds)
    next.delete(id)
    bulkPendingIds = next
  }

  function updateBulkStatus(id: number, status: BulkTenderResult['status']) {
    bulkResults = bulkResults?.map((r) => (r.id === id ? { ...r, status } : r)) ?? null
  }

  // Cancels every tender in the currently-open results panel that is still
  // running. Scoped entirely by `bulkResults` (this batch's own rows), so it
  // can never touch a tender running as part of a different, unrelated
  // trigger. Each cancel is independent — one id failing to cancel doesn't
  // block the rest.
  async function cancelBulkAi() {
    const runningIds = bulkResults?.filter((r) => r.status === 'pending').map((r) => r.id) ?? []
    if (runningIds.length === 0) return
    await Promise.allSettled(
      runningIds.map(async (id) => {
        try {
          await aiAnalysisApi.cancel(id)
          stopPolling(id)
          const detail = await procurementsApi.get(id)
          procurements = procurements.map((p) =>
            p.id === id ? { ...p, aiScore: detail.aiScore } : p,
          )
          finishAi(id)
          clearBulkPending(id)
          updateBulkStatus(id, 'cancelled')
        } catch {
          // Best-effort per tender — the cancel call itself failed (e.g.
          // network blip), so this row is presumably still running; its
          // existing poll loop is left in place to keep tracking it instead
          // of being abandoned mid-flight.
        }
      }),
    )
  }

  // Closing the results panel is read as "done with this batch" — it exits
  // bulk selection entirely rather than leaving the checkboxes/toolbar
  // stranded on screen. Still-running tenders aren't affected: the
  // persistent spinner and the completion toast are driven by aiSpinnerIds
  // / the pollAi callback, both independent of bulk mode.
  function closeBulkResults() {
    exitBulkMode()
  }

  function openBulkResults() {
    if (!bulkResults) return
    bulkResultsVisible = true
  }

  onDestroy(() => {
    analysisPoller.dispose()
    if (listRefreshTimer !== null) clearInterval(listRefreshTimer)
    listRefreshController?.abort()
    if (statsRefreshTimer !== null) clearTimeout(statsRefreshTimer)
    statsRefreshController?.abort()
  })

  let currentPage = $derived(Math.floor(offset / pageSize) + 1)
  let totalPages = $derived(totalCount !== null ? Math.ceil(totalCount / pageSize) : null)
  let rangeFrom = $derived(procurements.length === 0 ? 0 : offset + 1)
  let rangeTo = $derived(offset + procurements.length)

  /* svelte-spa-router's `use:link` action already stashes the scroll offset
     into *this* history entry's state right before navigating to a tender's
     detail page (see its scrollstateHistoryHandler) — and clears it again the
     instant syncToUrl()'s replace() touches this entry, i.e. on any filter/
     sort/page change. So a value survives here only when we're mounting from
     a genuine "back" out of a tender opened off the *current* list view, never
     a fresh visit and never a view whose filters/paging changed since. Read it
     up front, before our own hydrateFromUrl()/syncToUrl() calls can touch it. */
  const savedScrollState = window.history.state as {
    __svelte_spa_router_scrollX?: number
    __svelte_spa_router_scrollY?: number
  } | null

  /* Deferred until the mount-time data (filters/sort/page already restored
     from the URL by hydrateFromUrl) has actually rendered — restoring earlier
     would scroll against the placeholder "Loading…" page, which is far
     shorter than the real list and can't be scrolled that far yet. */
  async function restoreListScroll() {
    const y = savedScrollState?.__svelte_spa_router_scrollY
    if (typeof y !== 'number') return
    const x = savedScrollState?.__svelte_spa_router_scrollX ?? 0
    await tick()
    requestAnimationFrame(() => window.scrollTo(x, y))
  }

  onMount(() => {
    const urlFilterId = hydrateFromUrl()
    const signal = start()
    void (async () => {
      // The active tender filter must be resolved — and checked against the
      // real filter list — before the first procurements fetch, since it
      // scopes that request's filterId. initActiveFilterId can resolve to a
      // stale id (a URL param, or a value remembered in localStorage from
      // before the filter was deleted, or from a wiped/reset database where
      // ids restart from 1) that no longer exists; sending that filterId
      // silently returns zero procurements instead of surfacing the problem.
      await Promise.all([initActiveFilterId(urlFilterId), loadTenderFilters(signal)])
      dropActiveFilterIfStale()
      await Promise.all([
        loadRuns(signal),
        loadSources(signal),
        loadSavedFilters(signal),
        loadProcurements(signal),
      ])
      await restoreListScroll()
      // Scheduled only after the initial load resolves, so the very first
      // background poll already sees whether a loaded run is running (and
      // picks the fast interval) instead of reading the still-empty `runs`.
      if (!signal.aborted) scheduleNextStatsRefresh()
    })()
    startListRefresh()
  })

  // A stale activeFilterId (its filter was deleted, or the database was
  // reset and ids restarted from 1) falls back to the *default* filter, not
  // Unfiltered — "Unfiltered" is a concept this feature introduced; before
  // it, Argus always showed only IT-relevant tenders, so an unrecoverable
  // active filter should land back on the same safe, known baseline a fresh
  // install resolves to (see initActiveFilterId), not on an unscoped view
  // that never existed before. Only falls through to Unfiltered if there is
  // somehow no default filter at all.
  function dropActiveFilterIfStale() {
    const current = get(activeFilterId)
    if (current === undefined || tenderFilters.some((f) => f.id === current)) return
    const defaultFilter = tenderFilters.find((f) => f.isDefault)
    setActiveFilterId(defaultFilter?.id)
  }

  async function loadRuns(signal: AbortSignal) {
    try {
      runs = await retrievalsApi.list({}, { signal })
    } catch {
      // recent-runs is supporting context; tolerate failure
    }
  }

  async function loadSources(signal: AbortSignal) {
    try {
      sources = await sourcesApi.list({ signal })
    } catch {
      // sources panel + filter dropdown can tolerate failure
    }
  }

  async function loadSavedFilters(signal: AbortSignal) {
    try {
      savedFilters = await savedFiltersApi.list({ signal })
    } catch {
      // saved filters are optional context
    }
  }

  async function loadTenderFilters(signal: AbortSignal) {
    try {
      tenderFilters = await tenderFiltersApi.list({ signal })
    } catch {
      // the active-filter selector degrades to "no other filters to pick"
    }
  }

  function selectActiveFilter(id: number | undefined) {
    setActiveFilterId(id)
    offset = 0
    syncToUrl()
    void loadProcurements()
  }

  const activeFilterOptions = $derived([
    { value: undefined, label: $tStore('views.dashboard.activeFilterNone') },
    ...tenderFilters.map((f) => ({
      value: f.id,
      label: f.isDefault ? `${f.name} (${$tStore('views.tenderFilters.defaultBadge')})` : f.name,
    })),
  ])

  // The default arg opens a fresh scope for paging / filter-driven reloads
  // (cancelling any in-flight listing so a slow response can't clobber the
  // newer state); the mount load passes its shared signal explicitly.
  async function loadProcurements(signal: AbortSignal = start(), isSilent = false) {
    if (!isSilent) {
      loading = true
      errorMsg = null
    }
    try {
      const filters: ProcurementFiltersType =
        isSilent && lastAppliedProcurementFilters
          ? lastAppliedProcurementFilters
          : {
              ...baseFilters,
              // Archived tenders (deadline passed) live in the Archive view,
              // not the Dashboard — not overridable via baseFilters, which
              // has no UI control for it.
              archived: false,
              // The active CPV tender filter scopes which "universe" of
              // tenders All Procurements shows — independent of the ad-hoc
              // search/status filters in baseFilters.
              filterId: $activeFilterId,
              sort: sortField,
              order: sortField ? sortDir : undefined,
              limit: pageSize,
              offset,
            }
      if (!isSilent) lastAppliedProcurementFilters = { ...filters }
      const filterBase = {
        ...baseFilters,
        archived: false,
        filterId: $activeFilterId,
        sort: sortField,
        order: sortField ? sortDir : undefined,
      }
      // The Top Sources panel must keep ranking every platform even while one
      // is selected as the source filter — the backend's countBySource
      // collapses to that single source once sourceId is present (see
      // ProcurementService.countBySource), which would make the panel
      // disappear out from under the very selection driving it.
      const sourceTotalsFilters = { ...filterBase, sourceId: undefined }
      if (!isSilent) lastAppliedFilterBase = { ...filterBase }
      const [page, total, srcTotals] = await Promise.all([
        procurementsApi.list(filters, { signal }),
        procurementsApi.count(filterBase, { signal }),
        procurementsApi.sourceTotals(sourceTotalsFilters, { signal }),
      ])
      if (signal.aborted) return
      procurements = page
      totalCount = total
      sourceTotals = srcTotals
      hasMore = offset + page.length < total
    } catch (err) {
      if (signal.aborted) return
      if (!isSilent) errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted && !isSilent) loading = false
    }
  }

  function startListRefresh() {
    if (listRefreshTimer !== null) clearInterval(listRefreshTimer)
    listRefreshTimer = setInterval(() => {
      void refreshProcurementsSilently()
    }, LIST_REFRESH_INTERVAL_MS)
  }

  async function refreshProcurementsSilently() {
    // Automatic AI scoring happens outside this UI, so quietly refresh only the
    // visible page to pick up completed scores. Keep this independent from
    // count/source-total requests so an unrelated dashboard query can't block
    // the table from reflecting fresh AI results.
    if (loading || listRefreshInFlight || !lastAppliedProcurementFilters) return
    listRefreshController?.abort()
    const controller = new AbortController()
    listRefreshController = controller
    listRefreshInFlight = true
    try {
      const page = await procurementsApi.list(lastAppliedProcurementFilters, {
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      procurements = page
      if (totalCount !== null) {
        hasMore = offset + page.length < totalCount
      }
    } catch {
      // Background refresh is best-effort; the normal filter/page actions still
      // surface errors.
    } finally {
      if (listRefreshController === controller) {
        listRefreshController = null
      }
      if (!controller.signal.aborted) {
        listRefreshInFlight = false
      }
    }
  }

  function scheduleNextStatsRefresh(): void {
    const delay = hasRunning ? STATS_FAST_INTERVAL_MS : STATS_HEARTBEAT_INTERVAL_MS
    statsRefreshTimer = setTimeout(() => {
      void refreshStatsSilently().finally(scheduleNextStatsRefresh)
    }, delay)
  }

  // Background refresh for Recent runs / Last successful run / Top sources /
  // total count. Never flips `loading` and never touches `procurements`,
  // paging, scroll, or URL state — only the stats-shaped state below, in
  // place, so the All Procurements table and the user's scroll position stay
  // exactly where they are. Errors are swallowed (best-effort); they'll
  // surface on the next explicit load/filter action.
  async function refreshStatsSilently(): Promise<void> {
    if (loading || statsRefreshInFlight || !lastAppliedFilterBase) return
    statsRefreshController?.abort()
    const controller = new AbortController()
    statsRefreshController = controller
    statsRefreshInFlight = true
    try {
      const filterBase = lastAppliedFilterBase
      const sourceTotalsFilters = { ...filterBase, sourceId: undefined }
      const [latestRuns, total, srcTotals] = await Promise.all([
        retrievalsApi.list({}, { signal: controller.signal }),
        procurementsApi.count(filterBase, { signal: controller.signal }),
        procurementsApi.sourceTotals(sourceTotalsFilters, { signal: controller.signal }),
      ])
      if (controller.signal.aborted) return
      runs = latestRuns
      totalCount = total
      sourceTotals = srcTotals
      hasMore = offset + procurements.length < total
    } catch {
      // Background refresh is best-effort; the normal filter/page actions still
      // surface errors.
    } finally {
      if (statsRefreshController === controller) {
        statsRefreshController = null
      }
      if (!controller.signal.aborted) {
        statsRefreshInFlight = false
      }
    }
  }

  function applyFilters() {
    const min = toScore(baseFilters.minValueEur)
    const max = toScore(baseFilters.maxValueEur)
    // Catch an inverted custom value range client-side — cheaper and
    // friendlier than round-tripping to the backend's raw
    // "Invalid value range: minValueEur must be <= maxValueEur" message,
    // which names internal field names and is never translated.
    if (min !== undefined && max !== undefined && min > max) {
      errorMsg = $tStore('views.procurements.filters.valueRangeInvalid')
      return
    }
    offset = 0
    syncToUrl()
    void loadProcurements()
  }

  // Top Sources click-to-filter — a standalone action on the same sourceId
  // dimension as the "Source" field in the filter panel below (the backend
  // only supports one sourceId per request, so there is no meaningful way to
  // combine two independent source selections). Deliberately does not touch
  // any other field on baseFilters.
  function selectTopSource(sourceId: number) {
    baseFilters.sourceId = sourceId
    sourceLockedByTopSources = true
    applyFilters()
  }

  function deselectTopSource() {
    baseFilters.sourceId = undefined
    sourceLockedByTopSources = false
    applyFilters()
  }

  // Toggle sorting for a column. First click on a new column sorts descending;
  // clicking the active column flips the direction. Paging resets to page 1 so
  // the reordered list is read from the top.
  function toggleSort(field: ProcurementSortField) {
    if (sortField === field) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc'
    } else {
      sortField = field
      sortDir = 'desc'
    }
    offset = 0
    syncToUrl()
    void loadProcurements()
  }

  // The Top Sources-driven source filter is standalone — only its own
  // Deselect (or the mirrored ✕ in the filter panel) can cancel it, the same
  // way this function never touched it before Top Sources existed. So when
  // locked, everything else resets but sourceId survives.
  function clearFilters() {
    baseFilters = sourceLockedByTopSources ? { sourceId: baseFilters.sourceId } : {}
    activeSavedFilterId = undefined
    valueRangeChoice = 'custom'
    sortField = undefined
    sortDir = 'desc'
    offset = 0
    syncToUrl()
    void loadProcurements()
  }

  // Selecting a saved filter only populates the draft filter fields, the same
  // as picking a Source or Country — it must NOT trigger a request itself.
  // The user still has to click "Filter" (applyFilters) to run the query, so
  // they can review/tweak the populated values first.
  function selectSavedFilter(id: number | undefined) {
    activeSavedFilterId = id
    // Selecting a saved filter is its own deliberate source choice, separate
    // from Top Sources — the dropdown must not stay locked to whatever Top
    // Sources had selected before this.
    sourceLockedByTopSources = false
    if (id === undefined) {
      baseFilters = {}
      return
    }
    const found = savedFilters.find((f) => f.id === id)
    if (!found) return
    const cfg = found.filterConfig
    baseFilters = {
      search: cfg.search,
      sourceId: cfg.sourceId,
      status: cfg.status,
      procurementType: cfg.procurementType,
      country: cfg.country,
      cpv: cfg.cpv,
      minAiScore: cfg.minAiScore,
      minValueEur: cfg.minValueEur,
      maxValueEur: cfg.maxValueEur,
      currency: cfg.currency,
    }
    valueRangeChoice = 'custom'
  }

  function changePageSize(next: number) {
    if (next === pageSize) return
    // Reset to first page so users don't land on an empty offset when they
    // increase the page size on a deep page.
    pageSize = next
    offset = 0
    syncToUrl()
    void loadProcurements()
  }

  function prevPage() {
    if (offset === 0) return
    offset = Math.max(0, offset - pageSize)
    syncToUrl()
    void loadProcurements()
    scrollToTop()
  }

  function nextPage() {
    if (!hasMore) return
    offset = offset + pageSize
    syncToUrl()
    void loadProcurements()
    scrollToTop()
  }

  function goToPage(page: number) {
    offset = (page - 1) * pageSize
    syncToUrl()
    void loadProcurements()
    scrollToTop()
  }

  // Paging swaps in an entirely new set of tenders - leaving the scroll
  // position where it was (e.g. at the bottom, from reading the previous
  // page) would strand the user mid-list instead of at the new page's start.
  // Scrolls to the top of the table itself (not the whole page) so the KPI
  // strip / recent runs above it don't get shoved back into view on every
  // page change — per review feedback on this change.
  let tableAnchor = $state<HTMLElement | undefined>(undefined)

  function scrollToTop() {
    tableAnchor?.scrollIntoView({ block: 'start' })
  }

  // KPI derivations
  let pageCount = $derived(procurements.length)
  let activeSources = $derived(sources.filter((s) => s.enabled).length)
  let lastSuccessful = $derived(runs.find((r) => r.status === 'success'))
  // Drives the background stats refresh's poll interval — fast while a run
  // is in flight, a slow heartbeat otherwise.
  let hasRunning = $derived(runs.some((r) => r.status === 'running'))

  // sourceTotals.sourceName is the source's internal `name` (not
  // displayName) — matches Source.name, which is what the backend groups by.
  let sourceIdByName = $derived(new Map(sources.map((s) => [s.name, s.id])))
  // Official platform names for display only — grouping/sorting above stays keyed by internal name.
  let sourceLabels = $derived(buildSourceLabels(sources))

  let topSources = $derived(
    sourceTotals.length > 0
      ? sourceTotals.map((s) => ({
          sourceName: s.sourceName,
          found: s.total,
          sourceId: sourceIdByName.get(s.sourceName),
        }))
      : (() => {
          const tally = new Map<string, { found: number; sourceId: number }>()
          for (const p of procurements) {
            const existing = tally.get(p.sourceName)
            tally.set(p.sourceName, {
              found: (existing?.found ?? 0) + 1,
              sourceId: p.sourceId,
            })
          }
          return Array.from(tally.entries())
            .map(([sourceName, { found, sourceId }]) => ({ sourceName, found, sourceId }))
            .sort((a, b) => b.found - a.found)
        })(),
  )

  // Show the latest run for every active source. A fixed row limit (or repeated
  // manual runs for one source) must not crowd EOJN or another enabled source
  // out of the health overview.
  let recentRuns = $derived(latestRunPerActiveSource(runs, sources))

  // ---------------------------------------------------------------------------
  // URL-based filter persistence
  //
  // Filter state is encoded in the URL query string so it survives:
  //   • forward navigation to a tender detail view + back navigation
  //   • hard browser refresh
  //   • link sharing
  //
  // syncToUrl()    — builds URLSearchParams from current state, calls replace()
  //                 so no extra history entry is pushed on every filter change.
  // hydrateFromUrl() — called once on mount to restore state from URL params.
  // ---------------------------------------------------------------------------

  const VALID_SORT_FIELDS: ProcurementSortField[] = [
    'value',
    'type',
    'published',
    'deadline',
    'aiScore',
  ]

  function syncToUrl() {
    const params = new URLSearchParams()

    if (baseFilters.search) params.set('q', baseFilters.search)
    if (baseFilters.sourceId !== undefined) params.set('source', String(baseFilters.sourceId))
    if (baseFilters.procurementType) params.set('type', baseFilters.procurementType)
    if (baseFilters.country) params.set('country', baseFilters.country)
    if (baseFilters.cpv) params.set('cpv', baseFilters.cpv)
    const score = toScore(baseFilters.minAiScore)
    if (score !== undefined) params.set('score', String(score))

    // 'cur' omitted = "Any currency" (no Value range dropdown at all, just
    // bare Min/Max). 'vr' only distinguishes a named preset from "Any
    // value" (free-form Min/Max) — the bounds themselves round-trip via
    // minVal/maxVal regardless of currency state.
    if (baseFilters.currency) {
      params.set('cur', baseFilters.currency)
      if (valueRangeChoice !== 'custom') params.set('vr', valueRangeChoice)
    }
    const minVal = toScore(baseFilters.minValueEur)
    if (minVal !== undefined) params.set('minVal', String(minVal))
    const maxVal = toScore(baseFilters.maxValueEur)
    if (maxVal !== undefined) params.set('maxVal', String(maxVal))

    if (sortField !== undefined) {
      params.set('sort', sortField)
      params.set('dir', sortDir)
    }

    if (offset > 0) params.set('offset', String(offset))
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pageSize))
    if (activeSavedFilterId !== undefined) params.set('savedFilter', String(activeSavedFilterId))
    if ($activeFilterId !== undefined) params.set('filter', String($activeFilterId))
    if (filtersOpen) params.set('open', '1')

    const qs = params.toString()
    void replace(qs ? `/?${qs}` : '/')
  }

  function hydrateFromUrl(): number | undefined {
    const raw = get(querystring) ?? ''
    if (!raw) return undefined

    const params = new URLSearchParams(raw)
    const filters: ProcurementFiltersType = {}

    const search = params.get('q')
    if (search) filters.search = search

    const sourceId = params.get('source')
    if (sourceId !== null) {
      const n = Number(sourceId)
      if (!isNaN(n)) filters.sourceId = n
    }

    const type = params.get('type')
    if (type === 'simple' || type === 'public') filters.procurementType = type

    const country = params.get('country')
    if (ALLOWED_COUNTRY_CODES.includes(country as CountryCode))
      filters.country = country as CountryCode

    const cpv = params.get('cpv')
    if (cpv) filters.cpv = cpv

    const score = params.get('score')
    if (score !== null) {
      const n = Number(score)
      if (!isNaN(n)) filters.minAiScore = n
    }

    const cur = params.get('cur')
    if (cur === 'EUR' || cur === 'CHF') filters.currency = cur
    const vr = params.get('vr')
    const preset = cur && vr ? VALUE_RANGE_PRESETS.find((p) => p.id === vr) : undefined
    if (preset) {
      // Named preset under a specific currency: look up its bounds so the
      // filter is correct even from a bare URL (without going through the
      // dropdown).
      valueRangeChoice = preset.id
      filters.minValueEur = preset.min ?? undefined
      filters.maxValueEur = preset.max ?? undefined
    } else {
      // "Any value" under a specific currency, or Any-currency's bare
      // Min/Max — either way, whatever bounds are in the URL apply as-is.
      valueRangeChoice = 'custom'
      const minVal = params.get('minVal')
      const maxVal = params.get('maxVal')
      if (minVal !== null) {
        const n = Number(minVal)
        if (!isNaN(n)) filters.minValueEur = n
      }
      if (maxVal !== null) {
        const n = Number(maxVal)
        if (!isNaN(n)) filters.maxValueEur = n
      }
    }

    baseFilters = filters

    const sort = params.get('sort')
    if (sort !== null && (VALID_SORT_FIELDS as string[]).includes(sort)) {
      sortField = sort as ProcurementSortField
      sortDir = params.get('dir') === 'asc' ? 'asc' : 'desc'
    }

    const offsetParam = params.get('offset')
    if (offsetParam !== null) {
      const n = Number(offsetParam)
      if (!isNaN(n) && n >= 0) offset = n
    }

    const pageSizeParam = params.get('pageSize')
    if (pageSizeParam !== null) {
      const n = Number(pageSizeParam)
      if ((PAGE_SIZES as readonly number[]).includes(n)) pageSize = n
    }

    const savedFilter = params.get('savedFilter')
    if (savedFilter !== null) {
      const n = Number(savedFilter)
      if (!isNaN(n)) activeSavedFilterId = n
    }

    let urlFilterId: number | undefined
    const filterParam = params.get('filter')
    if (filterParam !== null) {
      const n = Number(filterParam)
      if (!isNaN(n)) urlFilterId = n
    }

    if (params.get('open') === '1') filtersOpen = true

    // Auto-open the filter panel when any filter is active so the user can see
    // what is applied without having to expand it manually.
    const hasActiveFilters =
      filters.search ||
      filters.sourceId !== undefined ||
      filters.procurementType ||
      filters.country ||
      filters.cpv ||
      filters.minAiScore !== undefined ||
      filters.minValueEur !== undefined ||
      filters.maxValueEur !== undefined ||
      filters.currency !== undefined ||
      sortField !== undefined ||
      activeSavedFilterId !== undefined
    if (hasActiveFilters) filtersOpen = true

    return urlFilterId
  }
</script>

<section class="dashboard">
  <PageHeader
    title={$tStore('views.dashboard.title')}
    subtitle={$tStore('views.dashboard.subtitle')}
  >
    {#snippet actions()}
      <label class="active-filter">
        <span class="active-filter__label">{$tStore('views.dashboard.activeFilter')}</span>
        <Dropdown
          value={$activeFilterId}
          options={activeFilterOptions}
          onchange={selectActiveFilter}
        />
      </label>
    {/snippet}
  </PageHeader>

  <KpiStrip
    {pageCount}
    {totalCount}
    {activeSources}
    lastSuccessfulAt={lastSuccessful ? formatDateTime(lastSuccessful.completedAt) : null}
  />

  <div class="grid">
    <RecentRunsCard runs={recentRuns} {sourceLabels} bind:tableHeight={recentRunsTableHeight} />
    <TopSourcesCard
      {topSources}
      {sourceLabels}
      listMaxHeight={recentRunsTableHeight}
      selectedSourceId={baseFilters.sourceId}
      onSelect={selectTopSource}
      onDeselect={deselectTopSource}
    />
  </div>

  <article class="card all-procurements">
    <ProcurementFilters
      filters={baseFilters}
      bind:valueRangeChoice
      {activeSavedFilterId}
      {sources}
      {savedFilters}
      open={filtersOpen}
      {bulkMode}
      selectedCount={selectedIds.size}
      bulkSelectionLimit={MAX_BULK_SELECTION}
      {occupiedBulkSlots}
      hasBulkResults={bulkResults !== null}
      sourceLocked={sourceLockedByTopSources}
      onApply={applyFilters}
      onViewBulkResults={openBulkResults}
      onClear={clearFilters}
      onDeselectSource={deselectTopSource}
      onToggle={() => {
        filtersOpen = !filtersOpen
        syncToUrl()
      }}
      onSelectSavedFilter={selectSavedFilter}
      onToggleBulkMode={toggleBulkMode}
      onRunBulkAi={runBulkAi}
    />
    <div bind:this={tableAnchor}>
      <ProcurementTable
        {procurements}
        {loading}
        {errorMsg}
        {sortField}
        {sortDir}
        {aiPending}
        {bulkMode}
        {selectedIds}
        onSort={toggleSort}
        onRunAi={runAi}
        onDelete={confirmDelete}
        onCancel={cancelAi}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAllOnPage}
      />
    </div>
    <ProcurementPager
      {pageSize}
      {offset}
      {rangeFrom}
      {rangeTo}
      {currentPage}
      {totalPages}
      {totalCount}
      {hasMore}
      {loading}
      pageSizes={PAGE_SIZES}
      {bulkMode}
      selectedCount={selectedIds.size}
      bulkSelectionLimit={MAX_BULK_SELECTION}
      {occupiedBulkSlots}
      hasBulkResults={bulkResults !== null}
      onPageSizeChange={changePageSize}
      onPrev={prevPage}
      onNext={nextPage}
      onGoToPage={goToPage}
      onViewBulkResults={openBulkResults}
      onToggleBulkMode={toggleBulkMode}
      onRunBulkAi={runBulkAi}
    />
  </article>
</section>

<DeleteConfirmModal
  target={deleteTarget}
  inFlight={deleteInFlight}
  onConfirm={executeDeletion}
  onCancel={cancelDelete}
/>

<BulkAiResultsModal
  results={bulkResultsVisible ? bulkResults : null}
  onClose={closeBulkResults}
  onCancel={cancelBulkAi}
/>

<AiAnalysisSpinner visible={showAiSpinner} />

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .dashboard {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: v.$space-lg;

    @media (max-width: v.$bp-lg) {
      grid-template-columns: 1fr;
    }
  }

  .all-procurements {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;
  }

  .active-filter {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    font-size: 0.8125rem;
  }

  .active-filter__label {
    color: v.$color-text-muted;
    white-space: nowrap;
  }
</style>
